import React, { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Checkbox, FormControlLabel, Typography, useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import axiosInstant from '../../api';
import { IVideo } from '../../pages/admin/videos/hooks/useFetchVideos';
import { VIDEO_SORT_OPTIONS } from '../../pages/admin/videos/adminVideosV2/helper';
import { BrandFilter } from '../../pages/admin/videos/adminVideosV2/brandFilter';
import { ViewToggle } from '../ViewToggle/ViewToggle';
import { AspectRatioFilter } from '../../pages/admin/videos/adminVideosV2/aspectRatioFilter';
import { SelectedVideoV2 } from '../../pages/admin/videos/selectedVideoCardV2';
import SortControls from '../SortControls/SortControls';
import { useCommonParams } from '../../hooks/useCommonParams';
import { isAM, isInternal, VIDEO_V2_FILTER_KEYS } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { useVideosQuery } from './useVideosQuery';
import { VideoCard } from './VideoCard';
import { VideoMenuContext } from './VideoCardMenu';
import FilterPopover from './FilterPopover';
import { FilterChip } from './FilterChip';
import { useVideoTagName } from '../../pages/admin/videos/adminVideosV2/tagFilter';
import { UploadableVideoCard } from './VideoUpload';
import { DELETED_TOGGLE_LABEL } from './videoSoftDelete';

export interface VideoSectionFeatures {
  sort?: boolean;
  viewToggle?: boolean;
  filters?: boolean;
  /** 'always' (internal pages) | 'probe' (brand-gated) | 'off' */
  nonProject?: 'always' | 'probe' | 'off';
  /** Swap to the inline detail view when ?video= is present (§08). */
  inlineDetail?: boolean;
}

export interface VideoSectionProps {
  brandId?: number;
  features?: VideoSectionFeatures;
  defaultView?: 'grid' | 'list';
  emptyTitle?: string;
  emptyBody?: string;
  emptyCta?: { label: string; onClick: () => void };
  /** Context params that must not read as user-facing filters (proId, embed, productId…). */
  excludeFilterKeys?: string[];
  suppressExpertRow?: boolean;
  isExpertVideos?: boolean;
  isStoryVideos?: boolean;
  isProductVideos?: boolean;
  menuContext?: VideoMenuContext;
  onCountChange?: (count: number) => void;
  /** Drop the "{n} videos" total from the chips row, for a host that shows
   *  the total itself (the product page's heading pill). The row stays for
   *  active filter chips and the Non-project toggle, and collapses when
   *  neither is showing. Off everywhere else. */
  hideCount?: boolean;
  /** Draw the Non-project toggle inside this element (the host's heading)
   *  instead of the chips row. Its probe, state and URL param are unchanged;
   *  only its place moves. `null` = the host's element isn't mounted yet (the
   *  toggle waits). Left undefined everywhere else. */
  nonProjectToggleTarget?: HTMLElement | null;
  detailContainerClassName?: string;
}

const CHIP_LABELS: Record<string, string> = {
  proId: 'Expert',
  productId: 'Product',
  brandId: 'Brand',
  aspectRatio: 'Aspect',
  extraFilter: 'Extra',
  contentCode: 'Code',
  productCategoryId: 'Category',
  demo: 'Demo',
  embed: 'Story',
  search: 'Search',
  hasPdp: 'PDP URL',
  internalStatus: 'Internal Status',
  approved: 'Approval',
  tagId: 'Tag',
};
const VALUE_CHIPS = new Set(['aspectRatio', 'contentCode', 'search', 'extraFilter', 'internalStatus']);
// Boolean presence filters read as their option name, not "PDP URL: true".
const BOOL_CHIP_LABELS: Record<string, Record<string, string>> = {
  hasPdp: { true: 'Has PDP URL', false: 'No PDP URL' },
  approved: { true: 'Approved', false: 'Not Approved' },
};
const chipText = (key: string, value: string) => {
  const boolLabel = BOOL_CHIP_LABELS[key]?.[value];
  if (boolLabel) return boolLabel;
  return VALUE_CHIPS.has(key) ? `${CHIP_LABELS[key] || key}: ${value}` : CHIP_LABELS[key] || key;
};

const filterIc = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
    <path d="M4 6h16M7 12h10M10 18h4" />
  </svg>
);
const searchIc = (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
  </svg>
);

const SkeletonStack: FC = () => (
  <div className="border border-border-token rounded-xl bg-surface overflow-hidden animate-pulse">
    <div className="h-[120px] bg-track" />
    <div className="p-[13px] flex flex-col gap-[9px]">
      <div className="w-[72px] h-[18px] rounded-full bg-track" />
      <div className="w-full h-[13px] rounded bg-track" />
      <div className="w-3/5 h-[13px] rounded bg-track" />
    </div>
  </div>
);
const SkeletonRow: FC = () => (
  <div className="border border-border-token rounded-xl bg-surface p-3 flex items-start gap-3.5 animate-pulse">
    <div className="w-24 h-[54px] rounded-lg bg-track shrink-0" />
    <div className="flex-1 flex flex-col gap-[9px]">
      <div className="w-2/3 h-[15px] rounded bg-track" />
      <div className="w-1/2 h-[12px] rounded bg-track" />
    </div>
  </div>
);

/** §03/§06/§09 — the section chrome: sort, inline + popover filters, always-visible
 * active chips, honest states, Load More with an explicit count. */
export const VideoSection: FC<VideoSectionProps> = (props) => {
  const {
    brandId,
    features = {},
    defaultView = 'grid',
    emptyTitle = 'No videos yet',
    emptyBody = 'Videos appear here once a project reaches delivery.',
    emptyCta,
    excludeFilterKeys = [],
    suppressExpertRow,
    isExpertVideos,
    isStoryVideos,
    isProductVideos,
    menuContext,
    onCountChange,
    hideCount,
    nonProjectToggleTarget,
    detailContainerClassName,
  } = props;
  const { role } = useUser();
  const isMobile = useMediaQuery('(max-width: 767.95px)');

  const {
    searchParams,
    setSearchParams,
    sortType,
    getSortParam,
    setSortType,
    setSortDirection,
    view,
    setView,
  } = useCommonParams({
    defaultSortKey: 'dateCreated',
    defaultSortAscending: false,
    sortKeyMap: { dateCreated: 'createdAt' },
    defaultView,
  });

  const legacyMode = searchParams.get('nonProject') === 'true';
  // Admin "Deleted" view (?deleted=1): soft-deleted videos only, each with a
  // Restore action. admin | account manager admin, like the API — for anyone
  // else a stray ?deleted=1 is ignored here and never sent.
  const canSeeDeleted = !!features.filters && isAM(role);
  const deletedView = canSeeDeleted && searchParams.get('deleted') === '1';
  const videoId = useMemo(() => {
    const v = searchParams.get('video');
    return v ? parseInt(v, 10) || null : null;
  }, [searchParams]);

  // §09: one mobile rule — below md every surface uses rows; toggle disappears.
  const effectiveView = isMobile ? 'list' : (view as 'grid' | 'list');

  const { videos, totalCount, loading, loadingMore, error, hasMore, loadMore, retry, refreshSilently } =
    useVideosQuery({
    brandId,
    legacyMode,
    sort: features.sort ? getSortParam() : undefined,
    enabled: !(features.inlineDetail && videoId),
    deleted: deletedView,
  });

  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  // Non-project toggle visibility
  const [showNonProject, setShowNonProject] = useState(features.nonProject === 'always');
  useEffect(() => {
    setShowNonProject(features.nonProject === 'always');
    if (features.nonProject !== 'probe' || !brandId) return undefined;
    let live = true;
    axiosInstant
      .get(`/brands/${brandId}/s3files/tdc?limit=1&offset=0&nonProjectOnly=true`)
      .then((r) => live && setShowNonProject(r.data?.count > 0))
      .catch(() => live && setShowNonProject(false));
    return () => {
      live = false;
    };
  }, [features.nonProject, brandId]);

  const setLegacyMode = (checked: boolean) => {
    setSearchParams((params) => {
      if (checked) params.set('nonProject', 'true');
      else params.delete('nonProject');
      params.delete('page');
      params.delete('limit');
      return params;
    });
  };

  const setDeletedView = (checked: boolean) => {
    setSearchParams((params) => {
      if (checked) params.set('deleted', '1');
      else params.delete('deleted');
      params.delete('page');
      params.delete('limit');
      return params;
    });
  };

  const onFilterSelect = (key: string, id?: string) => {
    setSearchParams((params) => {
      params.delete('limit');
      params.delete('page');
      if (!id) params.delete(key);
      else params.set(key, id);
      return params;
    });
  };

  // Active REAL filters (context keys excluded) — drives chips, count badge, empty copy.
  const activeFilters = useMemo(() => {
    const keys = [...VIDEO_V2_FILTER_KEYS, 'search'].filter((k) => !excludeFilterKeys.includes(k));
    return keys
      .map((key) => ({ key, value: searchParams.get(key) || '' }))
      .filter((e) => !!e.value);
  }, [searchParams, excludeFilterKeys]);
  const tagName = useVideoTagName(isInternal(role) ? searchParams.get('tagId') || '' : '');

  const clearAllFilters = () => {
    setSearchParams((params) => {
      activeFilters.forEach((e) => params.delete(e.key));
      // nonProject narrows results like a filter, so "Clear all" must release
      // it — otherwise a bookmarked ?nonProject=true on a brand whose probe
      // returns 0 hides the checkbox and strands the user on an empty list.
      params.delete('nonProject');
      params.delete('page');
      return params;
    });
  };

  const openVideo = (video: IVideo) => {
    setSearchParams((params) => {
      params.set('video', String(video.id));
      return params;
    });
  };

  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  // FilterPopover's option lists fetch on mount, so it mounts on first open
  // and then stays mounted — reopening must not refetch four endpoints.
  const [filterOpened, setFilterOpened] = useState(false);
  // Entering the inline detail unmounts the popover; keep it unmounted on
  // return until the user opens it again — a kept-mounted popover would
  // refetch its option lists (while closed!) on every detail round-trip.
  useEffect(() => {
    if (videoId) setFilterOpened(false);
  }, [videoId]);

  // §08 — inline detail swap
  if (features.inlineDetail && videoId) {
    return (
      <Box className="flex-1 min-w-0">
        <SelectedVideoV2
          videoId={videoId}
          type="file"
          // The list that was just clicked is still in memory, so the loading
          // skeleton can reserve the right plate for a portrait video.
          aspectHint={videos.find((v) => v.id === videoId)?.aspect_ratio}
          // Every inlineDetail host — the expert profile, product detail and
          // story detail — renders this view INSIDE a page that already owns a
          // title and a breadcrumb. The detail view collapses accordingly (§04).
          nested
          containerClassName={detailContainerClassName}
          refetch={retry}
          onClose={() =>
            setSearchParams((params) => {
              params.delete('video');
              params.delete('selectedTab');
              return params;
            })
          }
        />
      </Box>
    );
  }

  const filterBtn = 'inline-flex items-center gap-2 h-10 px-[13px] border rounded-[10px] whitespace-nowrap transition-colors';
  const showChrome = features.sort || features.filters || features.viewToggle;

  // In the chips row, or portalled into the host's element when it passes
  // nonProjectToggleTarget (same element, state and handler either way).
  const nonProjectToggle = (
    <FormControlLabel
      className="!mr-0"
      control={<Checkbox size="small" checked={legacyMode} onChange={(e) => setLegacyMode(e.target.checked)} />}
      label={
        <Typography variant="body2" color="textSecondary">
          Non-project only
        </Typography>
      }
    />
  );
  const toggleOutside = nonProjectToggleTarget !== undefined;
  // A view switch, not a filter: "Clear all" keeps it, and it stays in the
  // chips row even where the Non-project toggle is drawn in the host's heading.
  const deletedToggle = canSeeDeleted && (
    <FormControlLabel
      className="!mr-0"
      control={<Checkbox size="small" checked={deletedView} onChange={(e) => setDeletedView(e.target.checked)} />}
      label={
        <Typography variant="body2" color="textSecondary">
          {DELETED_TOGGLE_LABEL}
        </Typography>
      }
    />
  );

  return (
    <Box className="flex flex-col gap-[18px] min-w-0">
      {showChrome && (
        <Box className="flex items-center flex-wrap gap-2.5">
          {features.sort && (
            <SortControls
              sortKey={sortType.key}
              isAscending={sortType.isAscending}
              onSortKeyChange={(key) => setSortType({ key, isAscending: sortType.isAscending })}
              onDirectionChange={setSortDirection}
              options={VIDEO_SORT_OPTIONS}
              selectWidth={175}
              showSelect
              dense
            />
          )}
          {features.filters && isInternal(role) && !excludeFilterKeys.includes('brandId') && (
            <BrandFilter onFilterSelect={onFilterSelect} queryParams="videos=true" />
          )}
          {features.filters && <AspectRatioFilter onFilterSelect={onFilterSelect} />}
          {features.filters && (
            <button
              type="button"
              onClick={(e) => {
                setFilterOpened(true);
                setFilterAnchor(e.currentTarget);
              }}
              className={classNames(filterBtn, 'border-border-token dark:border-muted bg-surface text-text-2 hover:text-accent-text hover:border-accent')}
              style={{ fontSize: 13 }}
            >
              {filterIc}
              More filters
              {activeFilters.length > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-full bg-accent font-bold"
                  style={{ fontSize: 10.5, color: 'var(--rail)' }}
                >
                  {activeFilters.length}
                </span>
              )}
            </button>
          )}
          <span className="flex-1" />
          {features.viewToggle && !isMobile && (
            <ViewToggle view={effectiveView} onViewChange={(v) => setView(v as 'grid' | 'list')} />
          )}
        </Box>
      )}

      {/* Active chips + count row (hidden when hideCount leaves it empty) */}
      <Box
        className={classNames(
          'flex items-center flex-wrap gap-[9px]',
          hideCount && activeFilters.length === 0 && (!showNonProject || toggleOutside) && !canSeeDeleted && 'hidden'
        )}
      >
        {activeFilters.length > 0 && (
          <>
            <span className="font-bold uppercase text-text-2" style={{ fontSize: 11, letterSpacing: '.08em' }}>
              Active
            </span>
            {activeFilters.map((e) => (
              <FilterChip
                key={e.key}
                label={e.key === 'tagId' && tagName ? `Tag: ${tagName}` : chipText(e.key, e.value)}
                tone={e.key === 'search' ? 'surface' : 'tint'}
                removeLabel={`Clear ${CHIP_LABELS[e.key] || e.key} filter`}
                onRemove={() => onFilterSelect(e.key, undefined)}
              />
            ))}
            <button
              type="button"
              onClick={clearAllFilters}
              className="h-7 px-[11px] rounded-full text-accent-text font-bold underline"
              style={{ fontSize: 12 }}
            >
              Clear all
            </button>
          </>
        )}
        <span className="flex-1 min-w-3" />
        {showNonProject && !toggleOutside && nonProjectToggle}
        {deletedToggle}
        {!hideCount && (
          <b className="text-text-1 font-bold tabular-nums" style={{ fontSize: 12.5 }}>
            {totalCount} {deletedView ? 'deleted ' : ''}video{totalCount === 1 ? '' : 's'}
          </b>
        )}
      </Box>
      {/* In a fragment: MUI Box's children PropType rejects a bare portal
          (a dev-only console error on the product page). */}
      {showNonProject && nonProjectToggleTarget && <>{createPortal(nonProjectToggle, nonProjectToggleTarget)}</>}

      {/* Results / states */}
      {(() => {
        if (loading) {
          if (effectiveView === 'grid') {
            return (
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <SkeletonStack key={i} />
                ))}
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          );
        }
        if (error && videos.length === 0) {
          return (
            <div className="flex flex-col items-center gap-[13px] px-5 py-[30px] border border-border-token rounded-xl bg-surface">
              <span className="text-down">
                <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 4 2.5 20h19z" />
                  <path d="M12 10v4" />
                  <circle cx="12" cy="17.2" r=".9" fill="currentColor" stroke="none" />
                </svg>
              </span>
              <div className="font-bold text-text-1 text-center" style={{ fontSize: 14.5 }}>
                Couldn&apos;t load videos
              </div>
              <p className="m-0 max-w-[300px] text-center text-text-2" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                The request failed. Your filters are still applied — nothing was lost.
              </p>
              <button
                type="button"
                onClick={retry}
                className="h-10 px-5 rounded-[9px] bg-accent font-bold"
                style={{ fontSize: 13, color: 'var(--rail)' }}
              >
                Try again
              </button>
            </div>
          );
        }
        if (videos.length === 0 && deletedView) {
          const narrowed = activeFilters.length > 0 || legacyMode;
          return (
            <div className="flex flex-col items-center gap-[13px] px-5 py-[30px] border border-border-token rounded-xl bg-surface">
              <span className="text-muted">{searchIc}</span>
              <div className="font-bold text-text-1 text-center" style={{ fontSize: 14.5 }}>
                {narrowed ? 'No deleted videos match these filters' : 'No deleted videos'}
              </div>
              <p className="m-0 max-w-[320px] text-center text-text-2" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                Videos an admin deletes show here until someone restores them.
              </p>
              <div className="flex flex-wrap justify-center gap-2.5">
                {narrowed && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="h-10 px-5 rounded-[9px] border border-border-token bg-surface text-text-1 font-bold"
                    style={{ fontSize: 13 }}
                  >
                    Clear all filters
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDeletedView(false)}
                  className="h-10 px-5 rounded-[9px] border border-border-token bg-surface text-text-1 font-bold"
                  style={{ fontSize: 13 }}
                >
                  Show live videos
                </button>
              </div>
            </div>
          );
        }
        if (videos.length === 0) {
          if (activeFilters.length > 0 || legacyMode) {
            return (
              <div className="flex flex-col items-center gap-[13px] px-5 py-[30px] border border-border-token rounded-xl bg-surface">
                <span className="text-muted">{searchIc}</span>
                <div className="font-bold text-text-1 text-center" style={{ fontSize: 14.5 }}>
                  No videos match these filters
                </div>
                <p className="m-0 max-w-[320px] text-center text-text-2" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                  Clear the filters to see everything.
                </p>
                {/* nonProject narrows the list like a filter, so the escape
                    hatch must appear for it too — its checkbox is hidden when
                    the brand probe returns 0, which would otherwise strand the
                    user on an empty list with no way back. */}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="h-10 px-5 rounded-[9px] border border-border-token bg-surface text-text-1 font-bold"
                  style={{ fontSize: 13 }}
                >
                  Clear all filters
                </button>
              </div>
            );
          }
          return (
            <div className="flex flex-col items-center gap-[13px] px-5 py-[30px] border border-border-token rounded-xl bg-surface">
              <span className="text-muted">
                <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2.5" y="5" width="19" height="14" rx="2" />
                  <path d="M7 5v14M17 5v14M2.5 12h19" />
                </svg>
              </span>
              <div className="font-bold text-text-1 text-center" style={{ fontSize: 14.5 }}>
                {emptyTitle}
              </div>
              <p className="m-0 max-w-[320px] text-center text-text-2" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                {emptyBody}
              </p>
              {emptyCta && (
                <button
                  type="button"
                  onClick={emptyCta.onClick}
                  className="h-10 px-5 rounded-[9px] bg-accent font-bold"
                  style={{ fontSize: 13, color: 'var(--rail)' }}
                >
                  {emptyCta.label}
                </button>
              )}
            </div>
          );
        }
        // §10 upload, at parity with the pre-redesign card. VideoCardV2 gated
        // the dropzone on ROLE ALONE (`canUpload = ADMIN | POST_PRODUCTION_ADMIN
        // | ACCOUNT_MANAGER_ADMIN`, then `rootProps = uploadsEnabled ? ... : {}`)
        // and passed the project straight through for the iconik path, letting
        // buildVideoFolder fall back to video.project. UploadableVideoCard
        // applies that same role gate internally and renders children untouched
        // when it fails, so wrapping unconditionally matches the old behavior.
        // NOTE: buildVideoFolder needs project.brands[].rmn to pick the RMN
        // tree, and neither video feed selects it — but that is PRE-EXISTING
        // (same builder, same feeds, unchanged by this branch), so it is a
        // separate bug, not a reason to withhold the affordance.
        // Drag-only: uploadSlot is deliberately NOT passed. The pre-redesign
        // card rendered no visible control either, and these feeds omit the
        // row's `project`, so buildVideoFolder cannot name the destination
        // correctly — advertising a button would make a misfiling path
        // discoverable. The Downloads tab, which does hydrate `project`, keeps
        // the §10 button.
        const renderCard = (video: IVideo, variant: 'browse' | 'row') => {
          // A deleted video is read-only apart from Restore: no detail view,
          // and no upload onto a slot that isn't live.
          if (deletedView) {
            return (
              <VideoCard
                key={video.id}
                video={video}
                variant={variant}
                onOpen={openVideo}
                refetch={retry}
                showExpert={!suppressExpertRow}
                menuContext={menuContext}
                deleted
              />
            );
          }
          const card = () => (
            <VideoCard
              video={video}
              variant={variant}
              onOpen={openVideo}
              refetch={retry}
              showExpert={!suppressExpertRow}
              menuContext={menuContext}
            />
          );
          return (
            <UploadableVideoCard
              key={video.id}
              video={video}
              project={(video as any).project}
              refetch={refreshSilently}
            >
              {card}
            </UploadableVideoCard>
          );
        };
        return (
          <>
            {effectiveView === 'grid' ? (
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                {videos.map((video) => renderCard(video, 'browse'))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {videos.map((video) => renderCard(video, 'row'))}
              </div>
            )}
            {error && videos.length > 0 && (
              <div className="flex items-center justify-center gap-3 py-3 text-down" style={{ fontSize: 13 }}>
                Couldn&apos;t load more videos.
                <button
                  type="button"
                  onClick={retry}
                  className="font-bold underline text-accent-text"
                >
                  Try again
                </button>
              </div>
            )}
            {(hasMore || totalCount > 0) && (
              <div className="flex flex-col items-center gap-[11px] pt-2">
                {hasMore && (
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="h-[42px] px-6 border border-border-token rounded-[10px] bg-surface text-text-1 font-bold disabled:opacity-50"
                    style={{ fontSize: 13.5 }}
                  >
                    {loadingMore ? 'Loading…' : 'Load 20 more'}
                  </button>
                )}
                <div className="text-text-2 tabular-nums" style={{ fontSize: 12 }}>
                  Showing {videos.length} of {totalCount}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {features.filters && filterOpened && (
        <FilterPopover
          anchorEl={filterAnchor}
          onClose={() => setFilterAnchor(null)}
          onFilterSelect={onFilterSelect}
          onClearAll={clearAllFilters}
          activeCount={activeFilters.length}
          isExpertVideos={isExpertVideos}
          isStoryVideos={isStoryVideos}
          isProductVideos={isProductVideos}
        />
      )}
    </Box>
  );
};

export default VideoSection;
